class ProductOfferShowcaseV2 extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.mainImage = this.querySelector('.product-offer-showcase__main-image');
    this.thumbnailButtons = Array.from(this.querySelectorAll('[data-thumbnail-button]'));
    this.flow = this.querySelector('[data-eg-po-v2]');
    this.configNode = this.querySelector('[data-eg-po-v2-config]');
    this.cards = Array.from(this.querySelectorAll('[data-eg-po-v2-card]'));
    this.segmentButtons = Array.from(this.querySelectorAll('[data-eg-po-v2-option]'));
    this.addButton = this.querySelector('[data-eg-po-v2-add]');
    this.errorMessage = this.querySelector('[data-error]');
    this.statusRegion = this.querySelector('[data-status]');
    this.modal = this.querySelector('[data-eg-po-v2-modal]');
    this.infoOpeners = Array.from(this.querySelectorAll('[data-eg-po-v2-info-open]'));
    this.infoClose = this.querySelector('[data-eg-po-v2-info-close]');
    this.config = this.parseConfig();
    this.suppressGlobalBodyClass = this.dataset.suppressGlobalBodyClass === 'true';

    this.onThumbnailClick = this.handleThumbnailClick.bind(this);
    this.onCardToggle = this.handleCardToggle.bind(this);
    this.onSegmentClick = this.handleSegmentClick.bind(this);
    this.onAddClick = this.handleAddClick.bind(this);
    this.onInfoOpen = this.handleInfoOpen.bind(this);
    this.onInfoKeydown = this.handleInfoKeydown.bind(this);
    this.onInfoClose = this.closeInfoModal.bind(this);
    this.onModalClick = this.handleModalClick.bind(this);
    this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.onVariantUpdate = this.handleVariantUpdate.bind(this);

    if (!this.suppressGlobalBodyClass) {
      document.body.classList.add('has-product-offer-showcase', 'has-product-offer-showcase-v2');
    }

    this.thumbnailButtons.forEach((button) => button.addEventListener('click', this.onThumbnailClick));
    this.cards.forEach((card) => {
      card.querySelector('[data-eg-po-v2-card-toggle]')?.addEventListener('click', this.onCardToggle);
    });
    this.segmentButtons.forEach((button) => button.addEventListener('click', this.onSegmentClick));
    this.addButton?.addEventListener('click', this.onAddClick);
    this.infoOpeners.forEach((opener) => {
      opener.addEventListener('click', this.onInfoOpen);
      opener.addEventListener('keydown', this.onInfoKeydown);
    });
    this.infoClose?.addEventListener('click', this.onInfoClose);
    this.modal?.addEventListener('click', this.onModalClick);
    document.addEventListener('keydown', this.onDocumentKeydown);

    if (this.dataset.useTemplateProduct === 'true') {
      document.addEventListener('variant:update', this.onVariantUpdate);
    }

    this.syncSegments();
    this.syncState();
  }

  disconnectedCallback() {
    this.thumbnailButtons?.forEach((button) => button.removeEventListener('click', this.onThumbnailClick));
    this.cards?.forEach((card) => {
      card.querySelector('[data-eg-po-v2-card-toggle]')?.removeEventListener('click', this.onCardToggle);
    });
    this.segmentButtons?.forEach((button) => button.removeEventListener('click', this.onSegmentClick));
    this.addButton?.removeEventListener('click', this.onAddClick);
    this.infoOpeners?.forEach((opener) => {
      opener.removeEventListener('click', this.onInfoOpen);
      opener.removeEventListener('keydown', this.onInfoKeydown);
    });
    this.infoClose?.removeEventListener('click', this.onInfoClose);
    this.modal?.removeEventListener('click', this.onModalClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    document.removeEventListener('variant:update', this.onVariantUpdate);

    if (!this.suppressGlobalBodyClass) {
      requestAnimationFrame(() => {
        if (!document.querySelector('product-offer-showcase-v2:not([data-suppress-global-body-class="true"])')) {
          document.body.classList.remove('has-product-offer-showcase-v2');
        }
        if (
          !document.querySelector(
            'product-offer-showcase, product-offer-showcase-v2:not([data-suppress-global-body-class="true"])'
          )
        ) {
          document.body.classList.remove('has-product-offer-showcase');
        }
      });
    }
  }

  parseConfig() {
    if (!this.configNode) return {};

    try {
      return JSON.parse(this.configNode.textContent || '{}');
    } catch (error) {
      console.error(error);
      return {};
    }
  }

  get activeCard() {
    return this.cards.find((card) => card.classList.contains('is-active')) || this.cards[0] || null;
  }

  get activeOptionKey() {
    const activeCard = this.activeCard;
    const selected = activeCard?.querySelector('.eg-po-v2__seg.is-active');
    return selected?.dataset.egPoV2Option || '';
  }

  get activeOption() {
    return this.config?.options?.[this.activeOptionKey] || null;
  }

  handleThumbnailClick(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement) || !(this.mainImage instanceof HTMLImageElement)) return;

    const imageSrc = button.dataset.imageSrc;
    const imageSrcset = button.dataset.imageSrcset;
    const imageAlt = button.dataset.imageAlt || this.mainImage.alt;

    if (!imageSrc) return;

    this.mainImage.src = imageSrc;
    if (imageSrcset) this.mainImage.srcset = imageSrcset;
    this.mainImage.alt = imageAlt;

    this.thumbnailButtons.forEach((thumbnail) => {
      const isActive = thumbnail === button;
      thumbnail.classList.toggle('is-active', isActive);
      thumbnail.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  handleCardToggle(event) {
    if (event.target instanceof Element && event.target.closest('[data-eg-po-v2-info-open]')) return;

    const card = event.currentTarget?.closest('[data-eg-po-v2-card]');
    if (!(card instanceof HTMLElement)) return;

    this.activateCard(card);
  }

  handleSegmentClick(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    const card = button.closest('[data-eg-po-v2-card]');
    const group = button.closest('.eg-po-v2__segments');
    if (!(card instanceof HTMLElement) || !(group instanceof HTMLElement)) return;

    group.querySelectorAll('[data-eg-po-v2-option]').forEach((item) => {
      const isSelected = item === button;
      item.classList.toggle('is-active', isSelected);
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    this.activateCard(card);
    this.clearError();
    this.syncSegments();
    this.syncState();
  }

  activateCard(card) {
    this.cards.forEach((item) => {
      const isActive = item === card;
      const toggle = item.querySelector('[data-eg-po-v2-card-toggle]');
      const radio = item.querySelector('.eg-po-v2__radio');

      item.classList.toggle('is-active', isActive);
      toggle?.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      radio?.classList.toggle('is-checked', isActive);
    });

    this.syncState();
  }

  syncSegments() {
    this.segmentButtons.forEach((button) => {
      const option = this.config?.options?.[button.dataset.egPoV2Option];
      if (!option) return;

      const price = button.querySelector('[data-eg-po-v2-price]');
      const compare = button.querySelector('[data-eg-po-v2-compare]');
      const perDay = button.querySelector('[data-eg-po-v2-per-day]');
      const discount = button.querySelector('[data-eg-po-v2-discount]');

      if (price) price.textContent = this.formatMoney(option.price);
      if (compare) {
        const showCompare = Number(option.compare) > Number(option.price);
        compare.textContent = showCompare ? this.formatMoney(option.compare) : '';
        compare.hidden = !showCompare;
      }
      if (perDay) perDay.textContent = this.formatPerDay(option.price, option.days);
      if (discount) {
        const discountPercent = Number(option.discountPercent) || 0;
        discount.textContent = discountPercent > 0 ? `${this.config.discountLabel || 'Save'} ${discountPercent}%` : '';
        discount.hidden = discountPercent <= 0;
      }
    });
  }

  syncState() {
    this.cards.forEach((card) => {
      const selected = card.querySelector('.eg-po-v2__seg.is-active');
      const option = this.config?.options?.[selected?.dataset.egPoV2Option || ''];
      if (!option) return;

      const isActive = card.classList.contains('is-active');
      const headPrice = card.querySelector('[data-eg-po-v2-head-price]');
      const headMain = card.querySelector('[data-eg-po-v2-head-main]');
      const headCompare = card.querySelector('[data-eg-po-v2-head-compare]');
      const quantity = card.querySelector('[data-eg-po-v2-qty]');
      const boxPrice = card.querySelector('[data-eg-po-v2-box-price]');
      const boxCompare = card.querySelector('[data-eg-po-v2-box-compare]');
      const bullet = card.querySelector('[data-eg-po-v2-bullet]');

      if (headPrice) headPrice.hidden = isActive;
      if (headMain) headMain.textContent = this.formatMoney(option.total);
      if (headCompare) {
        const showCompare = Number(option.compare) > Number(option.price);
        headCompare.textContent = showCompare ? this.formatMoney(option.compare) : '';
        headCompare.hidden = !showCompare;
      }
      if (quantity) quantity.textContent = option.quantity;
      if (boxPrice) boxPrice.textContent = this.formatMoney(option.price);
      if (boxCompare) {
        const showCompare = Number(option.compare) > Number(option.price);
        boxCompare.textContent = showCompare ? this.formatMoney(option.compare) : '';
        boxCompare.hidden = !showCompare;
      }
      if (bullet) bullet.textContent = option.bullet || option.planName || '';
    });

    const buttonPrice = this.querySelector('[data-eg-po-v2-button-price]');
    if (buttonPrice && this.activeOption) {
      buttonPrice.textContent = this.formatMoney(this.activeOption.total);
    }

    this.notifyStateChange();
  }

  formatMoney(cents) {
    const value = (Number(cents) || 0) / 100;
    const currency = this.config.currencyCode || 'USD';
    const currencySymbol = this.config.currencySymbol || '';
    const locale = this.config.locale || document.documentElement.lang || 'en';
    const currencyDisplay = 'narrowSymbol';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (error) {
      return `${currencySymbol}${value.toFixed(2)}`;
    }
  }

  formatPerDay(cents, days) {
    const normalizedDays = Math.max(1, Number(days) || 1);
    return `${this.formatMoney(Math.ceil((Number(cents) || 0) / normalizedDays))}/day`;
  }

  handleInfoOpen(event) {
    event.preventDefault();
    event.stopPropagation();
    this.openInfoModal();
  }

  handleInfoKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    this.openInfoModal();
  }

  openInfoModal() {
    if (!this.modal) return;

    this.modal.hidden = false;
    document.body.classList.add('eg-po-v2-modal-open');
    this.infoClose?.focus({ preventScroll: true });
  }

  closeInfoModal() {
    if (!this.modal) return;

    this.modal.hidden = true;
    document.body.classList.remove('eg-po-v2-modal-open');
  }

  handleModalClick(event) {
    if (event.target === this.modal) {
      this.closeInfoModal();
    }
  }

  handleDocumentKeydown(event) {
    if (event.key === 'Escape' && this.modal && !this.modal.hidden) {
      this.closeInfoModal();
    }
  }

  handleAddClick() {
    this.addSelectedOffer();
  }

  buildLineItems() {
    const option = this.activeOption;
    const main = this.config.main || {};
    const addon = this.config.addon || {};
    const items = [];
    const offerGroup = this.createOfferGroupId();

    if (!option) return items;

    const hasAddonProduct = Number(option.variantId || addon.variantId) > 0 && Number(option.quantity) > 0;

    if (Number(main.variantId) > 0) {
      items.push({
        id: Number(main.variantId),
        quantity: Math.max(1, Number(main.quantity) || 1),
        properties: this.buildProperties({
          group: offerGroup,
          role: 'main',
          planName: option.planName,
          removeLinkedRoles: hasAddonProduct ? ['addon'] : [],
        }),
      });
    }

    if (hasAddonProduct) {
      const addonItem = {
        id: Number(option.variantId || addon.variantId),
        quantity: Math.max(1, Number(option.quantity) || 1),
        properties: this.buildProperties({
          group: offerGroup,
          role: 'addon',
          planName: option.planName,
          removeLinkedRoles: ['main'],
        }),
      };

      if (Number(option.sellingPlanId) > 0) {
        addonItem.selling_plan = Number(option.sellingPlanId);
      }

      items.push(addonItem);
    }

    return items;
  }

  buildProperties({ group, role, planName, removeLinkedRoles = [] }) {
    const properties = {
      _offer_source: 'product-offer-showcase-v2',
      _offer_group: group,
      _offer_role: role,
      _offer_lock_quantity: 'true',
    };

    if (removeLinkedRoles.length > 0) {
      properties._offer_remove_linked_roles = removeLinkedRoles.join(',');
    }

    if (planName) {
      properties['Purchase option'] = planName;
    }

    return properties;
  }

  createOfferGroupId() {
    if (window.crypto?.randomUUID) {
      return `offer-v2-${window.crypto.randomUUID()}`;
    }

    return `offer-v2-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  }

  async addSelectedOffer() {
    if (this.isBusy) return;

    const route = this.config.routeCartAdd || this.dataset.routeCartAdd || '/cart/add.js';
    const items = this.buildLineItems();

    if (!items.length) {
      this.showError('Unable to add this item to the cart.');
      return;
    }

    this.setBusy(true);
    this.clearError();

    const cartSections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
    const requestBody = { items };

    if (cartSections.length > 0) {
      requestBody.sections = cartSections.join(',');
      requestBody.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(route, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(requestBody),
        credentials: 'same-origin',
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Unable to add this item to the cart.');
      }

      this.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: this.id,
            data: {
              source: 'product-offer-showcase-v2',
              itemCount: items.reduce((count, item) => count + item.quantity, 0),
              productId: this.dataset.mainProductId,
              sections: data.sections || {},
            },
          },
        })
      );
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      if (this.statusRegion) {
        this.statusRegion.textContent = 'Added to cart';
      }

      this.openCartDrawer();
    } catch (error) {
      this.showError(error.message || 'Unable to add this item to the cart.');
      console.error(error);
    } finally {
      this.setBusy(false);
    }
  }

  openCartDrawer() {
    const drawerComponent = document.querySelector('cart-drawer-component');
    if (drawerComponent instanceof HTMLElement) {
      if (typeof drawerComponent.open === 'function') {
        drawerComponent.open();
        return;
      }
      if (typeof drawerComponent.showDialog === 'function') {
        drawerComponent.showDialog();
        return;
      }
    }

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer instanceof HTMLElement) {
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        return;
      }
      if (typeof cartDrawer.show === 'function') {
        cartDrawer.show();
      }
    }
  }

  setBusy(nextState) {
    this.isBusy = nextState;
    this.classList.toggle('is-busy', nextState);
    if (this.addButton) {
      this.addButton.disabled = nextState;
      this.addButton.setAttribute('aria-busy', nextState ? 'true' : 'false');
    }
    this.notifyStateChange();
  }

  notifyStateChange() {
    this.dispatchEvent(
      new CustomEvent('product-offer-v2:state-change', {
        bubbles: true,
        detail: {
          activeOptionKey: this.activeOptionKey,
          activeOption: this.activeOption,
          isBusy: Boolean(this.isBusy),
        },
      })
    );
  }

  clearError() {
    if (!this.errorMessage) return;

    this.errorMessage.textContent = '';
    this.errorMessage.classList.add('hidden');
  }

  showError(message) {
    if (!this.errorMessage) return;

    this.errorMessage.textContent = message;
    this.errorMessage.classList.remove('hidden');

    if (this.statusRegion) {
      this.statusRegion.textContent = message;
    }
  }

  handleVariantUpdate(event) {
    if (this.dataset.useTemplateProduct !== 'true') return;

    const html = event?.detail?.data?.html;
    if (!html) return;

    const replacement =
      (typeof html.getElementById === 'function' && html.getElementById(this.id)) ||
      (typeof html.querySelector === 'function' ? html.querySelector(`[id="${this.id}"]`) : null);
    if (replacement instanceof HTMLElement) {
      this.replaceWith(replacement);
    }
  }
}

if (!customElements.get('product-offer-showcase-v2')) {
  customElements.define('product-offer-showcase-v2', ProductOfferShowcaseV2);
}
